<template>
  <PageLayout :title="$t('posts.title')" :subtitle="$t('posts.subtitle')">
    <template #actions>
      <router-link to="/posts/create" class="btn btn-primary">
        <i class="fas fa-plus mr-1"></i> {{ $t('posts.new_post') }}
      </router-link>
    </template>

    <ContentCard>
      <div class="posts-toolbar">
        <div class="posts-toolbar__row">
          <input
            v-model.trim="filters.q"
            type="search"
            class="form-control form-control-sm posts-toolbar__search"
            :placeholder="$t('posts.search_placeholder')"
            @keyup.enter="applyFilters"
          />
          <button type="button" class="btn btn-sm btn-primary" @click="applyFilters">
            {{ $t('common.filter') }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="toggleAdvanced">
            {{ showAdvanced ? $t('posts.hide_filters') : $t('posts.show_filters') }}
          </button>
          <button
            v-if="hasActiveFilters"
            type="button"
            class="btn btn-sm btn-link text-muted"
            @click="resetFilters"
          >
            {{ $t('posts.reset_filters') }}
          </button>
        </div>

        <div v-show="showAdvanced" class="posts-filters-advanced">
          <div class="posts-filters-advanced__grid">
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.col_status') }}</span>
              <select v-model="filters.status" class="form-control form-control-sm">
                <option value="">{{ $t('common.all') }}</option>
                <option value="draft">{{ $t('status.draft') }}</option>
                <option value="scheduled">{{ $t('status.scheduled') }}</option>
                <option value="published">{{ $t('status.published') }}</option>
                <option value="failed">{{ $t('status.failed') }}</option>
                <option value="pending_approval">{{ $t('status.pending_approval') }}</option>
              </select>
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_type') }}</span>
              <select v-model="filters.type" class="form-control form-control-sm">
                <option value="">{{ $t('common.all') }}</option>
                <option value="feed">Feed</option>
                <option value="story">Story</option>
                <option value="reel">Reel</option>
              </select>
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_account') }}</span>
              <select v-model.number="filters.social_account_id" class="form-control form-control-sm">
                <option :value="0">{{ $t('posts.all_accounts') }}</option>
                <option v-for="acc in accounts" :key="acc.id" :value="acc.id">
                  @{{ acc.username }} ({{ acc.platform }})
                </option>
              </select>
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_platform') }}</span>
              <select v-model="filters.platform" class="form-control form-control-sm">
                <option value="">{{ $t('posts.all_platforms') }}</option>
                <option v-for="p in platformOptions" :key="p" :value="p">{{ p }}</option>
              </select>
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_recurring') }}</span>
              <select v-model="filters.recurring" class="form-control form-control-sm">
                <option value="">{{ $t('posts.filter_recurring_all') }}</option>
                <option value="only">{{ $t('posts.filter_recurring_only') }}</option>
                <option value="exclude">{{ $t('posts.filter_recurring_none') }}</option>
              </select>
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_ai') }}</span>
              <select v-model="filters.ai_generated" class="form-control form-control-sm">
                <option value="">{{ $t('posts.filter_ai_all') }}</option>
                <option value="true">{{ $t('posts.filter_ai_yes') }}</option>
                <option value="false">{{ $t('posts.filter_ai_no') }}</option>
              </select>
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_date_from') }}</span>
              <input v-model="filters.date_from" type="date" class="form-control form-control-sm" />
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_date_to') }}</span>
              <input v-model="filters.date_to" type="date" class="form-control form-control-sm" />
            </label>
            <label class="posts-filter-field">
              <span class="posts-filter-field__label">{{ $t('posts.filter_sort') }}</span>
              <select v-model="filters.sort" class="form-control form-control-sm">
                <option value="created_desc">{{ $t('posts.sort_created_desc') }}</option>
                <option value="created_asc">{{ $t('posts.sort_created_asc') }}</option>
                <option value="scheduled_asc">{{ $t('posts.sort_scheduled_asc') }}</option>
                <option value="scheduled_desc">{{ $t('posts.sort_scheduled_desc') }}</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div v-if="loading" class="state-loading" role="status" aria-live="polite" aria-busy="true">{{ $t('common.loading') }}</div>
      <div v-else-if="loadError" class="state-error" role="alert">
        <div class="state-error__title">{{ $t('common.load_failed') }}</div>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="loadPosts">{{ $t('common.retry') }}</button>
      </div>
      <div v-else-if="posts.length === 0" class="text-center py-5 text-muted">
        {{ $t('posts.empty') }}
        <template v-if="!hasActiveFilters">
          <router-link to="/posts/create">{{ $t('posts.create_first') }}</router-link>
        </template>
      </div>
      <div v-else class="table-responsive posts-table-wrap">
        <table class="table table-hover posts-table mb-0">
          <thead>
            <tr>
              <th class="posts-table__col-media">{{ $t('posts.col_media') }}</th>
              <th>{{ $t('posts.col_caption') }}</th>
              <th>{{ $t('posts.col_account') }}</th>
              <th>{{ $t('posts.col_type') }}</th>
              <th>{{ $t('posts.col_status') }}</th>
              <th>{{ $t('posts.col_schedule') }}</th>
              <th class="text-right">{{ $t('posts.col_impressions') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="post in posts" :key="post.id">
              <td>
                <router-link :to="`/posts/${post.id}`" class="posts-table__thumb">
                  <img v-if="isImage(post)" :src="post.media_urls[0]" alt="" />
                  <video
                    v-else-if="isVideo(post)"
                    :src="post.media_urls[0]"
                    muted
                    playsinline
                    preload="metadata"
                  />
                  <span v-else class="posts-table__thumb-ph"><i :class="typeIcon(post.type)"></i></span>
                  <span v-if="isVideo(post)" class="posts-table__thumb-badge"><i class="fas fa-play"></i></span>
                  <span v-if="post.media_urls?.length > 1" class="posts-table__thumb-count">{{ post.media_urls.length }}</span>
                </router-link>
              </td>
              <td class="posts-table__caption-cell">
                <router-link :to="`/posts/${post.id}`" class="posts-table__caption">
                  {{ truncate(post.caption, 100) }}
                </router-link>
                <div v-if="post.hashtags?.length" class="posts-table__tags">
                  <span v-for="tag in post.hashtags.slice(0, 3)" :key="tag" class="badge badge-light mr-1">#{{ tag }}</span>
                </div>
                <div class="posts-table__flags">
                  <span v-if="post.ai_generated" class="badge badge-info">{{ $t('posts.ai_badge') }}</span>
                  <span v-if="post.recurring_post_id" class="badge badge-secondary">{{ $t('posts.recurring_badge') }}</span>
                </div>
              </td>
              <td>
                <div class="posts-table__account">
                  <img
                    v-if="post.social_account?.profile_picture_url"
                    :src="post.social_account.profile_picture_url"
                    class="posts-table__avatar"
                    alt=""
                  />
                  <span>@{{ post.social_account?.username || '-' }}</span>
                </div>
                <span v-if="post.social_account?.platform" class="badge badge-light text-capitalize">{{ post.social_account.platform }}</span>
              </td>
              <td><span class="text-capitalize">{{ post.type }}</span></td>
              <td><span class="badge" :class="statusClass(post.status)">{{ statusLabel(post.status) }}</span></td>
              <td class="posts-table__dates">
                <div><span class="text-muted small">{{ $t('posts.scheduled') }}</span> {{ formatDate(post.scheduled_at) }}</div>
                <div v-if="post.published_at" class="small text-muted">{{ formatDate(post.published_at) }}</div>
              </td>
              <td class="text-right">{{ impressionsFor(post) || '—' }}</td>
              <td class="posts-table__actions text-right">
                <router-link :to="`/posts/${post.id}`" class="btn btn-xs btn-outline-secondary">
                  {{ $t('common.view') }}
                </router-link>
                <button
                  v-if="canPublish(post)"
                  type="button"
                  class="btn btn-xs btn-success"
                  @click.prevent="onPublish(post.id)"
                >
                  {{ $t('common.publish') }}
                </button>
                <button
                  v-if="canDelete(post)"
                  type="button"
                  class="btn btn-xs btn-outline-danger"
                  @click.prevent="onDelete(post.id)"
                >
                  {{ $t('common.delete') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!loading && !loadError" class="text-muted small mb-0 mt-2">{{ $t('common.total_count', { count: posts.length }) }}</p>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

const defaultFilters = () => ({
  status: '',
  type: '',
  recurring: '',
  ai_generated: '',
  social_account_id: 0,
  platform: '',
  q: '',
  date_from: '',
  date_to: '',
  sort: 'created_desc',
})

export default {
  mixins: [i18nPage],
  data() {
    return {
      filters: defaultFilters(),
      showAdvanced: false,
      loadError: false,
    }
  },
  computed: {
    ...mapGetters('posts', ['posts', 'loading']),
    ...mapGetters('app', ['accounts']),
    ...mapGetters('analytics', { analyticsPosts: 'posts' }),
    platformOptions() {
      const set = new Set()
      for (const acc of this.accounts || []) {
        if (acc.platform) set.add(acc.platform)
      }
      return [...set].sort()
    },
    analyticsMap() {
      const map = {}
      for (const item of this.analyticsPosts) {
        if (item.post_id) map[item.post_id] = item
      }
      return map
    },
    hasActiveFilters() {
      const f = this.filters
      return !!(
        f.status ||
        f.type ||
        f.recurring ||
        f.ai_generated ||
        f.social_account_id ||
        f.platform ||
        f.q ||
        f.date_from ||
        f.date_to ||
        (f.sort && f.sort !== 'created_desc')
      )
    },
  },
  async created() {
    await this.fetchAccounts()
    await this.loadPosts()
    await this.fetchPostAnalytics()
  },
  methods: {
    ...mapActions('posts', ['fetchPosts', 'publishPost', 'deletePost']),
    ...mapActions('app', { fetchAccounts: 'fetchAccounts' }),
    ...mapActions('analytics', { fetchPostAnalytics: 'fetchPosts' }),
    queryParams() {
      const f = this.filters
      const params = {
        status: f.status,
        type: f.type,
        recurring: f.recurring,
        ai_generated: f.ai_generated,
        platform: f.platform,
        q: f.q,
        date_from: f.date_from,
        date_to: f.date_to,
        sort: f.sort,
      }
      if (f.social_account_id > 0) {
        params.social_account_id = f.social_account_id
      }
      return params
    },
    async loadPosts() {
      this.loadError = false
      try {
        await this.fetchPosts(this.queryParams())
      } catch {
        this.loadError = true
      }
    },
    applyFilters() {
      this.loadPosts()
    },
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced
    },
    resetFilters() {
      this.filters = defaultFilters()
      this.loadPosts()
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString(this.intlLocale)
    },
    statusClass(status) {
      const map = {
        draft: 'badge-secondary',
        scheduled: 'badge-primary',
        published: 'badge-success',
        failed: 'badge-danger',
        publishing: 'badge-info',
        pending_approval: 'badge-warning',
      }
      return map[status] || 'badge-light'
    },
    canPublish(post) {
      return ['draft', 'scheduled', 'failed'].includes(post.status)
    },
    canDelete(post) {
      return post.status === 'failed'
    },
    impressionsFor(post) {
      const a = this.analyticsMap[post.id]
      if (!a) return post.status === 'published' ? null : ''
      return this.formatNumber(a.impressions)
    },
    formatNumber(n) {
      if (n == null) return '0'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n.toLocaleString()
    },
    typeIcon(type) {
      if (type === 'story') return 'fas fa-circle'
      if (type === 'reel') return 'fas fa-film'
      return 'fas fa-image'
    },
    isImage(post) {
      const url = post.media_urls?.[0] || ''
      return url && !this.isVideo(post)
    },
    isVideo(post) {
      const url = post.media_urls?.[0] || ''
      return url && /\.(mp4|mov|webm)(\?|$)/i.test(url)
    },
    truncate(text, len) {
      if (!text) return this.$t('common.no_caption')
      return text.length > len ? `${text.slice(0, len)}…` : text
    },
    async onPublish(id) {
      const ok = await this.$confirm(this.$t('posts.publish_confirm'), {
        confirmText: this.$t('common.publish'),
      })
      if (!ok) return
      try {
        await this.publishPost(id)
        await this.loadPosts()
        this.$toast.success(this.$t('posts.publish_queued'))
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('posts.publish_failed'))
      }
    },
    async onDelete(id) {
      const ok = await this.$confirm(this.$t('posts.delete_confirm'), {
        confirmText: this.$t('common.delete'),
        danger: true,
      })
      if (!ok) return
      try {
        await this.deletePost(id)
        await this.loadPosts()
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('posts.delete_failed'))
      }
    },
  },
}
</script>

<style scoped>
.posts-toolbar {
  margin-bottom: 1rem;
}

.posts-toolbar__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.posts-toolbar__search {
  flex: 1 1 200px;
  max-width: 320px;
}

.posts-filters-advanced {
  margin-top: 0.85rem;
  padding: 0.85rem;
  border: 1px solid #ececec;
  border-radius: 10px;
  background: #fafafa;
}

.posts-filters-advanced__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.65rem 0.75rem;
}

.posts-filter-field {
  display: flex;
  flex-direction: column;
  margin: 0;
}

.posts-filter-field__label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--wc-text-muted);
  margin-bottom: 0.25rem;
}

.posts-table-wrap {
  border: 1px solid #ececec;
  border-radius: 10px;
  overflow: hidden;
}

.posts-table thead th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--wc-text-muted);
  border-top: none;
  white-space: nowrap;
}

.posts-table__col-media {
  width: 72px;
}

.posts-table__thumb {
  position: relative;
  display: block;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f3f3;
}

.posts-table__thumb img,
.posts-table__thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.posts-table__thumb-ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
}

.posts-table__thumb-badge,
.posts-table__thumb-count {
  position: absolute;
  bottom: 4px;
  padding: 1px 5px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 0.6rem;
}

.posts-table__thumb-badge {
  left: 4px;
}

.posts-table__thumb-count {
  right: 4px;
}

.posts-table__caption {
  color: #222;
  font-weight: 500;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.posts-table__caption:hover {
  text-decoration: underline;
}

.posts-table__tags {
  margin-top: 0.25rem;
}

.posts-table__flags .badge {
  margin-right: 0.25rem;
  margin-top: 0.25rem;
}

.posts-table__account {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  margin-bottom: 0.2rem;
}

.posts-table__avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
}

.posts-table__dates {
  font-size: 0.8rem;
  white-space: nowrap;
}

.posts-table__actions {
  white-space: nowrap;
}

.posts-table__actions .btn {
  margin-left: 0.25rem;
}

@media (max-width: 991px) {
  .posts-table__caption-cell {
    min-width: 140px;
  }
}
</style>
